import { REGEX } from "@/backend/lib/regex";
import { ObjectCustomField } from "@/backend/models/customFields/objectCustomField";
import { Validation } from "@/backend/models/validation";
import { useParameterStore, ValidationStatus } from "@/frontend/store/ParameterStore";
import { I18n } from "@/i18n/I18n";
import { ParameterSubjectType } from "@/types/parameterTypes";
import { useShallow } from "zustand/react/shallow";
import { LoadingSpinner } from "../LoadingSpinner";
import { ValidationsFailedIcon } from "./ValidationsFailedIcon";
import { ValidationsPassingIcon } from "./ValidationsPassingIcon";
import { Parameter } from "@/backend/models/parameter";

export function StepValidationsSummary(props: { stepId: string }) {
  const { stepId } = props;

  return (
    <div className='validation-items'>
      <StepValidationsSummaryList stepId={stepId} />
    </div>
  );
}

export function StepValidationsSummaryList(props: { stepId: string }) {
  const { stepId } = props;
  const validations = useParameterStore(useShallow(state => state.validations.filter((v) => v.subjectId === stepId)));
  const validationStatuses = useParameterStore(useShallow(state => state.getValidationStatusMapForSubject(stepId)));

  if (validations.length === 0) return null;

  const statusArray = [...validationStatuses.values()];
  const anyRunning = statusArray.some(v => v === ValidationStatus.RUNNING);
  const allPassing = statusArray.every(v => v === ValidationStatus.PASSING);
  const failingValidations = validations.filter((v) => validationStatuses.get(v.id) === ValidationStatus.FAILING);
  const failingValidationsMissingMessagesAndTitles = failingValidations.filter((v) => !v.failMessage && !v.title);
  const allMissingPassMessagesAndTitles = validations.every(v => !v.passMessage && !v.title);

  if (anyRunning) {
    return (
      <div className='validation-item'>
        <LoadingSpinner />
        <span className='validation-item-message'>{I18n.get('validationsBeingEvaluated')}</span>
      </div>
    );
  }

  if (allPassing && allMissingPassMessagesAndTitles) {
    return (
      <div className='validation-item'>
        <ValidationsPassingIcon />
        <span className='validation-item-title-passed'>{I18n.get('stepValidationsAllPassed')}</span>
      </div>
    );
  }

  return (
    <>
      {validations.map(v => (
        <ValidationItem key={v.id} validation={v} />
      ))}
      {failingValidationsMissingMessagesAndTitles.length > 0 && failingValidations.length === failingValidationsMissingMessagesAndTitles.length && (
        <div className='validation-item'>
          <ValidationsFailedIcon />
          <span className='validation-item-title-failed'>{I18n.get('stepFailingValidations')}</span>
        </div>
      )}
    </>
  );
}

function ValidationItem(props: { validation: Validation }) {
  const { validation } = props;
  const status = useParameterStore(useShallow(state => state.getValidationStatus(validation.id)));
  const params = useParameterStore(useShallow(state =>
    state.parametersForSubject(validation.id, ParameterSubjectType.VALIDATION)
  ));

  if (status === ValidationStatus.PASSING) {
    if (!validation.passMessage && !validation.title) {
      return null;
    }
    return (
      <div className='validation-item'>
        <ValidationsPassingIcon />
        <div>
          { validation.title && <span className='validation-item-title-passed'>{validation.title}</span> }
          <span className='validation-item-message'>{parseMessage(validation.passMessage, params)}</span>
        </div>
      </div>
    );
  }

  if (status === ValidationStatus.FAILING) {
    if (!validation.failMessage && !validation.title) {
      return null;
    }

    return (
      <div className='validation-item'>
        <ValidationsFailedIcon />
        <div>
          { validation.title && <span className='validation-item-title-failed'>{validation.title}</span> }
          <span className='validation-item-message'>{parseMessage(validation.failMessage || '', params)}</span>
        </div>
      </div>
    );
  }
}

function parseMessage(message: string, params: Parameter[]) {
  return message.split(/(<<[^>>]+>>)/).map((part) => {
    if (part.match(REGEX.PARAMETER_IDENTIFIER)) {
      const splitPart = part.replace(/<|>/g, '').split('.');
      const identifier = splitPart[0];
      const param = params.find(p => p.identifier === identifier);
      if (!param) return part;
      if (splitPart.length > 1) {
        const field = param.fields[0];
        if (!(field instanceof ObjectCustomField)) return part;
        const object = field.objectInstance.properties.find(property => property.key === splitPart[1]);
        if (object) return object.value;
      }
      if (param) return param.concatArray() || I18n.get('NULL');
    }
    return part;
  }).join('');
}

